import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import Text from '@/components/common/Text';
import Pagination from '@/components/common/Pagination';
import MonthlyBillTable from '@/components/admin/MonthlyBillTable';
import MonthlyBillFilters, { type MonthlyBillFilterType } from '@/components/admin/MonthlyBillFilters';
import MonthlyBillStats from '@/components/admin/MonthlyBillStats';
import { getMonthlyBills } from '@/apis/admin';
import { QUERY_KEY } from '@/constants/queryKey';
import * as S from './style';

const AdminMonthlyBillPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Sync filters with URL params
  const currentPage = parseInt(searchParams.get('page') || '1');
  const currentLimit = parseInt(searchParams.get('limit') || '10');
  
  const [filters, setFilters] = useState<MonthlyBillFilterType & { page: number; limit: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }>({
    page: currentPage,
    limit: currentLimit,
    status: searchParams.get('status') || undefined,
    payment_method: searchParams.get('payment_method') || undefined,
    start_date: searchParams.get('start_date') || undefined,
    end_date: searchParams.get('end_date') || undefined,
    billing_month: searchParams.get('billing_month') || undefined,
    user_id: searchParams.get('user_id') ? parseInt(searchParams.get('user_id')!) : undefined,
    company_id: searchParams.get('company_id') ? parseInt(searchParams.get('company_id')!) : undefined,
    search: searchParams.get('search') || undefined,
    min_amount: searchParams.get('min_amount') ? parseInt(searchParams.get('min_amount')!) : undefined,
    max_amount: searchParams.get('max_amount') ? parseInt(searchParams.get('max_amount')!) : undefined,
    overdue_only: searchParams.get('overdue_only') === 'true',
    pending_confirmation_only: searchParams.get('pending_confirmation_only') === 'true',
    sortBy: searchParams.get('sortBy') || undefined,
    sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || undefined,
  });

  const handleFilterChange = (newFilters: MonthlyBillFilterType) => {
    const updatedFilters: MonthlyBillFilterType & { page: number; limit: number } = { 
      ...filters,
      ...newFilters,
      page: 1,
      limit: filters.limit || 10,
    };
    setFilters(updatedFilters);
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });
    setSearchParams(params);
  };

  const handleReset = () => {
    const resetFilters = {
      page: 1,
      limit: 10,
      status: undefined,
      payment_method: undefined,
      start_date: undefined,
      end_date: undefined,
      billing_month: undefined,
      user_id: undefined,
      search: undefined,
      min_amount: undefined,
      max_amount: undefined,
      overdue_only: false,
      pending_confirmation_only: false,
      sortBy: undefined,
      sortOrder: undefined,
    };
    setFilters(resetFilters);
    setSearchParams({});
  };

  // Sync filters when URL params change (e.g., from pagination)
  React.useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1');
    const urlLimit = parseInt(searchParams.get('limit') || '10');
    
    if (filters.page !== urlPage || filters.limit !== urlLimit) {
      setFilters(prev => ({
        ...prev,
        page: urlPage,
        limit: urlLimit,
      }));
    }
  }, [searchParams]);

  return (
    <S.Container>
      <Text fontWeight={700} fontSize='1.5625rem' margin='0 0 1.25rem 0'>
        월간 청구서 관리
      </Text>

      <MonthlyBillStats />

      <MonthlyBillFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleReset}
      />

      <MonthlyBillTable 
        filters={filters} 
        onSortChange={(sortBy, sortOrder) => {
          const updatedFilters = {
            ...filters,
            sortBy,
            sortOrder,
            page: 1, // Reset to page 1 when sorting changes
          };
          setFilters(updatedFilters);
          const params = new URLSearchParams();
          Object.entries(updatedFilters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '' && value !== false) {
              if (Array.isArray(value)) {
                params.append(key, value.join(','));
              } else {
                params.append(key, String(value));
              }
            }
          });
          setSearchParams(params);
        }}
      />

      <MonthlyBillPaginationWrapper filters={filters} />
    </S.Container>
  );
};

// Separate component for pagination to avoid re-rendering table
const MonthlyBillPaginationWrapper = ({ filters }: { filters: MonthlyBillFilterType & { page: number; limit: number } }) => {
  const { data } = useQuery({
    queryKey: [QUERY_KEY.ADMIN.MONTHLY_BILLS, filters],
    queryFn: () => getMonthlyBills(filters),
  });

  if (!data?.data || data.data.total === 0) {
    return null;
  }

  return (
    <Pagination
      page={filters.page}
      totalCount={data.data.total}
      size={filters.limit}
      showInfo={true}
    />
  );
};

export default AdminMonthlyBillPage;

